import { BotController, visibleWorldForBot } from "./bot";
import { sweptCircleIntersectsCircle } from "./collision";
import {
  reflectHeading,
  separatedPosition,
  type Contact,
} from "./collision-response";
import {
  DEFAULT_COMPETITIVE_CONFIG,
  mergeCompetitiveConfig,
  type CompetitiveConfig,
} from "./config";
import { FixedStepClock } from "./fixed-step";
import {
  add,
  distance,
  distanceSquared,
  fromAngle,
  normalizeAngle,
  scale,
  vec,
  type Vec2,
} from "./math";
import {
  byId,
  advanceSnakeKinematics,
  cloneFood,
  cloneSnapshot,
  cloneSnake,
  createDefaultMatchEntities,
  foodValueAt,
} from "./match-entities";
import {
  buildMatchDebugSnapshot,
  buildMatchResult,
  buildMatchSnapshot,
  buildRankings,
} from "./match-snapshots";
import {
  ENDLESS_RESPAWN_SECONDS,
  processEndlessRespawns,
} from "./match-respawn";
import { detectMatchCollisions, type PendingDeath } from "./match-collisions";
import { SeededRng } from "./rng";
import { extendTrail, sampleTrail } from "./trail";
import { snakeWidthForLength } from "./tuning";
import type {
  BodySegment,
  DeathRecord,
  Food,
  MatchDebugSnapshot,
  MatchMode,
  MatchPhase,
  MatchRanking,
  MatchResult,
  MatchSnapshot,
  ReadonlyMatchSnapshot,
  SnakeIntent,
  SnakeState,
} from "./types";

export {
  advanceSnakeKinematics,
  createSnake,
  foodValueAt,
  turnRateForLength,
} from "./match-entities";
export type { SnakeCreation } from "./match-entities";
export { snakeWidthForLength } from "./tuning";

export interface MatchEngineOptions {
  readonly seed: string | number;
  readonly config?: Partial<CompetitiveConfig>;
  readonly snakes?: readonly SnakeState[];
  readonly foods?: readonly Food[];
  readonly readySeconds?: number;
  readonly mode?: MatchMode;
  readonly leoAssist?: boolean;
  readonly maintainNaturalFood?: boolean;
}

export interface MatchRenderState {
  readonly previous: ReadonlyMatchSnapshot;
  readonly current: ReadonlyMatchSnapshot;
  readonly interpolationAlpha: number;
}

interface MutableMatchStats {
  maxLength: number;
  foodCollected: number;
  eliminations: number;
  deaths: number;
  aliveSeconds: number;
}

const MAX_ACTIVE_LENGTH = 5_000;
const MAX_FOOD_ENTITIES = 1_800;
const MAX_ENDLESS_DEATH_RECORDS = 128;
const CONTACT_COOLDOWN_SECONDS = 0.12;

export class MatchEngine {
  readonly config: CompetitiveConfig;
  readonly mode: MatchMode;
  private readonly random: SeededRng;
  private readonly clock: FixedStepClock;
  private readonly snakesById = new Map<string, SnakeState>();
  private readonly foodsById = new Map<string, Food>();
  private readonly intents = new Map<string, SnakeIntent>();
  private readonly botControllers = new Map<string, BotController>();
  private readonly statsBySnakeId = new Map<string, MutableMatchStats>();
  private readonly maintainNaturalFood: boolean;
  private readonly respawnDueBySnakeId = new Map<string, number>();
  private readonly protectionUntilBySnakeId = new Map<string, number>();
  private readonly contactCooldownUntil = new Map<string, number>();
  private readonly lastSpawnPointBySnakeId = new Map<string, Vec2>();
  private deaths: DeathRecord[] = [];
  private phase: MatchPhase;
  private readyRemainingSeconds: number;
  private desiredAssistEnabled: boolean;
  private assistEnabled: boolean;
  private assisted: boolean;
  private elapsedSeconds = 0;
  private stepIndex = 0;
  private generatedEntitySequence = 0;
  private lastBodySegments: BodySegment[] = [];
  private lastSpatialBucketCount = 0;
  private previousRenderSnapshot!: ReadonlyMatchSnapshot;
  private currentRenderSnapshot!: ReadonlyMatchSnapshot;
  private cachedDebugSnapshot!: Omit<MatchDebugSnapshot, "interpolationAlpha">;

  constructor(options: MatchEngineOptions) {
    this.config = mergeCompetitiveConfig(options.config);
    this.mode = options.mode ?? "signal";
    this.maintainNaturalFood = Boolean(options.maintainNaturalFood);
    this.random = new SeededRng(options.seed);
    this.clock = new FixedStepClock(this.config.fixedStepSeconds);
    this.readyRemainingSeconds = Math.max(0, options.readySeconds ?? 0);
    this.phase = this.readyRemainingSeconds > 0 ? "ready" : "playing";
    this.desiredAssistEnabled = Boolean(options.leoAssist);
    this.assistEnabled = Boolean(options.leoAssist);
    this.assisted = Boolean(options.leoAssist);

    for (const snakeInput of options.snakes ?? []) {
      if (this.snakesById.has(snakeInput.id))
        throw new Error(`duplicate-snake:${snakeInput.id}`);
      const snake = cloneSnake(snakeInput);
      snake.spawnGeneration ??= 0;
      snake.respawnRemainingSeconds ??= 0;
      if ((snake.protectionRemainingSeconds ?? 0) > 0) {
        this.protectionUntilBySnakeId.set(
          snake.id,
          this.elapsedSeconds + (snake.protectionRemainingSeconds ?? 0),
        );
      }
      this.snakesById.set(snake.id, snake);
      this.lastSpawnPointBySnakeId.set(snake.id, { ...snake.position });
      this.statsBySnakeId.set(snake.id, {
        maxLength: snake.length,
        foodCollected: 0,
        eliminations: 0,
        deaths: 0,
        aliveSeconds: 0,
      });
      this.intents.set(snake.id, {
        targetHeading: snake.targetHeading,
        boosting: false,
      });
      if (snake.controller === "bot") {
        this.botControllers.set(
          snake.id,
          new BotController(
            snake.id,
            options.seed,
            this.config.botReactionSeconds,
          ),
        );
      }
    }
    for (const foodInput of options.foods ?? []) {
      if (this.foodsById.has(foodInput.id))
        throw new Error(`duplicate-food:${foodInput.id}`);
      this.foodsById.set(foodInput.id, cloneFood(foodInput));
    }
    this.currentRenderSnapshot = this.buildSnapshot();
    this.previousRenderSnapshot = this.currentRenderSnapshot;
    this.cachedDebugSnapshot = this.buildDebugSnapshot();
  }

  static createDefault(
    seed: string | number,
    options: { readonly mode?: MatchMode; readonly readySeconds?: number } = {},
  ): MatchEngine {
    const config = DEFAULT_COMPETITIVE_CONFIG;
    const { snakes, foods } = createDefaultMatchEntities(seed, config);
    return new MatchEngine({
      seed,
      config,
      snakes,
      foods,
      mode: options.mode,
      readySeconds: options.readySeconds ?? 3,
      maintainNaturalFood: true,
    });
  }

  get snapshot(): MatchSnapshot {
    return cloneSnapshot(this.currentRenderSnapshot);
  }

  get renderState(): MatchRenderState {
    return {
      previous: this.previousRenderSnapshot,
      current: this.currentRenderSnapshot,
      interpolationAlpha: this.clock.interpolationAlpha,
    };
  }

  private buildSnapshot(): MatchSnapshot {
    return buildMatchSnapshot({
      phase: this.phase,
      readyRemainingSeconds: this.readyRemainingSeconds,
      mode: this.mode,
      assistEnabled: this.assistEnabled,
      assisted: this.assisted,
      elapsedSeconds: this.elapsedSeconds,
      matchDurationSeconds: this.config.matchDurationSeconds,
      snakes: this.sortedSnakes(),
      foods: this.sortedFoods(),
      deaths: this.deaths,
      rankings: this.rankings(),
      respawnRemaining: (snakeId) =>
        Math.max(
          0,
          (this.respawnDueBySnakeId.get(snakeId) ?? this.elapsedSeconds) -
            this.elapsedSeconds,
        ),
      protectionRemaining: (snakeId) =>
        Math.max(
          0,
          (this.protectionUntilBySnakeId.get(snakeId) ?? this.elapsedSeconds) -
            this.elapsedSeconds,
        ),
    });
  }

  get debugSnapshot(): MatchDebugSnapshot {
    return {
      ...this.cachedDebugSnapshot,
      interpolationAlpha: this.clock.interpolationAlpha,
    };
  }

  private buildDebugSnapshot(): Omit<MatchDebugSnapshot, "interpolationAlpha"> {
    const tideRadius = this.resonanceRadius();
    const snakes = this.sortedSnakes();
    const player = snakes.find((snake) => snake.controller === "human");
    const playerSpawn = player
      ? (this.lastSpawnPointBySnakeId.get(player.id) ?? player.position)
      : null;
    return buildMatchDebugSnapshot({
      stepIndex: this.stepIndex,
      botCount: this.botControllers.size,
      spatialBucketCount: this.lastSpatialBucketCount,
      snakes,
      bodySegments: this.lastBodySegments,
      deaths: this.deaths,
      playerSpawn,
      config: this.config,
      foods: this.sortedFoods(),
      elapsedSeconds: this.elapsedSeconds,
      tideRadius,
    });
  }

  snake(id: string): SnakeState | undefined {
    const snake = this.snakesById.get(id);
    if (!snake) return undefined;
    const cloned = cloneSnake(snake);
    cloned.respawnRemainingSeconds = Math.max(
      0,
      (this.respawnDueBySnakeId.get(id) ?? this.elapsedSeconds) -
        this.elapsedSeconds,
    );
    cloned.protectionRemainingSeconds = Math.max(
      0,
      (this.protectionUntilBySnakeId.get(id) ?? this.elapsedSeconds) -
        this.elapsedSeconds,
    );
    return cloned;
  }

  resultFor(snakeId: string): MatchResult | undefined {
    const snake = this.snakesById.get(snakeId);
    const stats = this.statsBySnakeId.get(snakeId);
    if (!snake || !stats) return undefined;
    return buildMatchResult(
      snake,
      stats,
      this.deaths,
      this.resultRankings(),
      this.elapsedSeconds,
      this.mode,
      this.assisted,
    );
  }

  setIntent(snakeId: string, intent: SnakeIntent): void {
    const snake = this.snakesById.get(snakeId);
    if (!snake || snake.controller !== "human") return;
    this.intents.set(snakeId, {
      targetHeading: normalizeAngle(intent.targetHeading),
      boosting: this.phase === "ready" ? false : Boolean(intent.boosting),
    });
  }

  /** Applied on the next authoritative tick. Persistence/entitlement gates live above this seam. */
  setLeoAssist(enabled: boolean): void {
    this.desiredAssistEnabled = Boolean(enabled);
  }

  endMatch(): void {
    if (this.phase === "ended") return;
    this.phase = "ended";
    for (const snake of this.snakesById.values()) snake.boosting = false;
    this.previousRenderSnapshot = this.currentRenderSnapshot;
    this.currentRenderSnapshot = this.buildSnapshot();
    this.cachedDebugSnapshot = this.buildDebugSnapshot();
  }

  advance(realDeltaSeconds: number): void {
    this.clock.advance(realDeltaSeconds, (stepSeconds) =>
      this.simulateStep(stepSeconds),
    );
  }

  private simulateStep(stepSeconds: number): void {
    if (this.phase === "ended") return;
    this.previousRenderSnapshot = this.currentRenderSnapshot;
    if (this.phase === "ready") {
      this.readyRemainingSeconds = Math.max(
        0,
        this.readyRemainingSeconds - stepSeconds,
      );
      if (this.readyRemainingSeconds <= 1e-9) {
        this.readyRemainingSeconds = 0;
        this.phase = "playing";
      }
      this.currentRenderSnapshot = this.buildSnapshot();
      this.cachedDebugSnapshot = this.buildDebugSnapshot();
      return;
    }
    this.assistEnabled = this.desiredAssistEnabled;
    if (this.assistEnabled) this.assisted = true;
    const stepEndsAt = this.elapsedSeconds + stepSeconds;
    this.processRespawns(stepEndsAt);
    for (const snake of this.snakesById.values()) {
      const protectedUntil = this.protectionUntilBySnakeId.get(snake.id);
      if (protectedUntil !== undefined && protectedUntil <= stepEndsAt + 1e-9)
        this.protectionUntilBySnakeId.delete(snake.id);
      snake.protectionRemainingSeconds = Math.max(
        0,
        (this.protectionUntilBySnakeId.get(snake.id) ?? stepEndsAt) -
          stepEndsAt,
      );
      snake.respawnRemainingSeconds = Math.max(
        0,
        (this.respawnDueBySnakeId.get(snake.id) ?? stepEndsAt) - stepEndsAt,
      );
      if (snake.alive) {
        const stats = this.statsBySnakeId.get(snake.id);
        if (stats) stats.aliveSeconds += stepSeconds;
      }
    }
    for (const [key, until] of this.contactCooldownUntil) {
      if (until <= stepEndsAt) this.contactCooldownUntil.delete(key);
    }
    this.stepIndex += 1;
    this.applyResonanceTide();
    this.updateBotIntents();
    const previousPositions = new Map(
      this.sortedSnakes().map((snake) => [snake.id, { ...snake.position }]),
    );

    for (const snake of this.sortedSnakes()) {
      if (!snake.alive) continue;
      const intent = this.intents.get(snake.id) ?? {
        targetHeading: snake.heading,
        boosting: false,
      };
      advanceSnakeKinematics(snake, intent, this.config, stepSeconds);

      if (snake.boosting) this.applyBoostCost(snake, stepSeconds);
      snake.width = snakeWidthForLength(snake.length);
      snake.trail = extendTrail(snake.trail, snake.position, snake.length);
    }

    const pendingDeaths = this.detectDeaths(previousPositions, stepEndsAt);
    this.applyDeaths(pendingDeaths, stepEndsAt);
    this.collectFood(previousPositions);
    if (this.maintainNaturalFood) this.replenishNaturalFood();

    for (const snake of this.snakesById.values()) {
      const stats = this.statsBySnakeId.get(snake.id);
      if (stats) stats.maxLength = Math.max(stats.maxLength, snake.length);
    }

    this.elapsedSeconds =
      this.mode === "signal"
        ? Math.min(this.config.matchDurationSeconds, stepEndsAt)
        : stepEndsAt;
    if (
      this.mode === "signal" &&
      this.elapsedSeconds + 1e-9 >= this.config.matchDurationSeconds
    ) {
      this.elapsedSeconds = this.config.matchDurationSeconds;
      this.phase = "ended";
    }
    this.currentRenderSnapshot = this.buildSnapshot();
    this.cachedDebugSnapshot = this.buildDebugSnapshot();
  }

  private updateBotIntents(): void {
    const snakes = this.sortedSnakes();
    const foods = this.sortedFoods();
    for (const [snakeId, controller] of [
      ...this.botControllers.entries(),
    ].sort()) {
      const snake = this.snakesById.get(snakeId);
      if (!snake?.alive) continue;
      const view = visibleWorldForBot(
        snake,
        snakes,
        foods,
        this.elapsedSeconds,
        this.config.botPerceptionRadius,
        this.config.arenaRadius,
      );
      this.intents.set(snakeId, controller.decide(view));
    }
  }

  private isProtected(snakeId: string, atSeconds: number): boolean {
    return (this.protectionUntilBySnakeId.get(snakeId) ?? 0) > atSeconds + 1e-9;
  }

  private processRespawns(atSeconds: number): void {
    if (this.mode !== "endless") return;
    processEndlessRespawns({
      atSeconds,
      respawnDueBySnakeId: this.respawnDueBySnakeId,
      protectionUntilBySnakeId: this.protectionUntilBySnakeId,
      snakesById: this.snakesById,
      lastSpawnPointBySnakeId: this.lastSpawnPointBySnakeId,
      intents: this.intents,
      botControllers: this.botControllers,
      config: this.config,
      random: this.random,
    });
  }

  private syncTrailHead(snake: SnakeState): void {
    const points = [...snake.trail.points];
    if (points.length === 0) points.push({ ...snake.position });
    else points[points.length - 1] = { ...snake.position };
    snake.trail = { points };
  }

  private bounce(
    snake: SnakeState,
    contact: Contact,
    contactKey: string,
    atSeconds: number,
    share = 1,
  ): void {
    snake.position = separatedPosition(snake.position, contact, share);
    this.syncTrailHead(snake);
    if ((this.contactCooldownUntil.get(contactKey) ?? 0) > atSeconds) return;
    snake.heading = reflectHeading(snake.heading, contact.normal);
    snake.targetHeading = snake.heading;
    this.intents.set(snake.id, {
      targetHeading: snake.heading,
      boosting: snake.boosting,
    });
    this.contactCooldownUntil.set(
      contactKey,
      atSeconds + CONTACT_COOLDOWN_SECONDS,
    );
  }

  private applyBoostCost(snake: SnakeState, stepSeconds: number): void {
    const availableLength = Math.max(
      0,
      snake.length - this.config.minimumBoostLength,
    );
    const cost = Math.min(
      availableLength,
      this.config.boostCostPerSecond * stepSeconds,
    );
    snake.length -= cost;
    snake.boostFragmentCarry += cost;
    while (snake.boostFragmentCarry + 1e-9 >= this.config.boostFragmentCost) {
      snake.boostFragmentCarry -= this.config.boostFragmentCost;
      this.addGeneratedFood({
        position: add(
          snake.position,
          scale(fromAngle(snake.heading), -snake.width * 1.5),
        ),
        radius: 2.5,
        baseValue:
          this.config.boostFragmentCost / this.config.growthPerFoodValue,
        source: "boost",
        resonantUntil: 0,
      });
    }
  }

  private detectDeaths(
    previousPositions: ReadonlyMap<string, Vec2>,
    atSeconds: number,
  ): Map<string, PendingDeath> {
    const alive = this.sortedSnakes().filter((snake) => snake.alive);
    const result = detectMatchCollisions({
      alive,
      previousPositions,
      atSeconds,
      config: this.config,
      assistEnabled: this.assistEnabled,
      isProtected: (snakeId, seconds) => this.isProtected(snakeId, seconds),
      headRadius: (snake) => this.headRadius(snake),
      bounce: (snake, contact, key, seconds, share) =>
        this.bounce(snake, contact, key, seconds, share),
      contactCooldownSeconds: CONTACT_COOLDOWN_SECONDS,
      setHeadingAfterBoundaryBounce: (snake, contact, key, seconds) => {
        snake.position = contact.point;
        this.syncTrailHead(snake);
        if ((this.contactCooldownUntil.get(key) ?? 0) <= seconds) {
          snake.heading = reflectHeading(snake.heading, contact.normal);
          snake.targetHeading = snake.heading;
          this.intents.set(snake.id, {
            targetHeading: snake.heading,
            boosting: snake.boosting,
          });
          this.contactCooldownUntil.set(
            key,
            seconds + CONTACT_COOLDOWN_SECONDS,
          );
        }
      },
      contactCooldownUntil: this.contactCooldownUntil,
      buildBodySegments: (snakes, excludeNewestHeadMotion) =>
        this.buildBodySegments(snakes, excludeNewestHeadMotion),
    });
    this.lastBodySegments = result.bodySegments;
    this.lastSpatialBucketCount = result.spatialBucketCount;
    return result.pendingDeaths;
  }

  private buildBodySegments(
    snakes: readonly SnakeState[],
    excludeNewestHeadMotion = false,
  ): BodySegment[] {
    const segments: BodySegment[] = [];
    for (const snake of snakes) {
      const segmentLimit = excludeNewestHeadMotion
        ? snake.trail.points.length - 1
        : snake.trail.points.length;
      for (let index = 1; index < segmentLimit; index += 1) {
        const start = snake.trail.points[index - 1];
        const end = snake.trail.points[index];
        if (!start || !end) continue;
        segments.push({
          snakeId: snake.id,
          segmentIndex: index - 1,
          start,
          end,
          radius: snake.width * this.config.bodyRadiusFactor,
        });
      }
    }
    return segments;
  }

  private applyDeaths(
    pending: Map<string, PendingDeath>,
    atSeconds: number,
  ): void {
    for (const [snakeId, death] of [...pending.entries()].sort(
      ([left], [right]) => left.localeCompare(right),
    )) {
      const snake = this.snakesById.get(snakeId);
      if (!snake?.alive) continue;
      snake.alive = false;
      snake.boosting = false;
      snake.speed = 0;
      this.protectionUntilBySnakeId.delete(snakeId);
      const stats = this.statsBySnakeId.get(snakeId);
      if (stats) stats.deaths += 1;
      this.deaths.push({
        snakeId,
        reason: death.reason,
        ...(death.sourceSnakeId ? { sourceSnakeId: death.sourceSnakeId } : {}),
        atSeconds,
        position: { ...snake.position },
      });
      if (
        this.mode === "endless" &&
        this.deaths.length > MAX_ENDLESS_DEATH_RECORDS
      ) {
        this.deaths.splice(0, this.deaths.length - MAX_ENDLESS_DEATH_RECORDS);
      }
      if (death.sourceSnakeId) {
        const sourceStats = this.statsBySnakeId.get(death.sourceSnakeId);
        if (sourceStats) sourceStats.eliminations += 1;
      }
      for (const position of sampleTrail(
        snake.trail,
        Math.max(this.config.eliminationFragmentSpacing, snake.width),
      )) {
        this.addGeneratedFood({
          position,
          radius: 3.5,
          baseValue: this.config.eliminationFragmentValue,
          source: "elimination",
          resonantUntil: 0,
        });
      }
      if (this.mode === "endless") {
        this.respawnDueBySnakeId.set(
          snake.id,
          atSeconds + ENDLESS_RESPAWN_SECONDS,
        );
        snake.respawnRemainingSeconds = ENDLESS_RESPAWN_SECONDS;
      } else if (snake.controller === "human") {
        this.phase = "ended";
      }
    }
  }

  private collectFood(previousPositions: ReadonlyMap<string, Vec2>): void {
    const alive = this.sortedSnakes().filter((snake) => snake.alive);
    for (const food of this.sortedFoods()) {
      const candidates = alive
        .filter((snake) =>
          sweptCircleIntersectsCircle(
            previousPositions.get(snake.id) ?? snake.position,
            snake.position,
            this.headRadius(snake),
            food.position,
            food.radius,
          ),
        )
        .sort(
          (left, right) =>
            distanceSquared(left.position, food.position) -
              distanceSquared(right.position, food.position) ||
            left.id.localeCompare(right.id),
        );
      const winner = candidates[0];
      if (!winner) continue;
      winner.length = Math.min(
        MAX_ACTIVE_LENGTH,
        winner.length +
          foodValueAt(food, this.elapsedSeconds, this.config) *
            this.config.growthPerFoodValue,
      );
      winner.width = snakeWidthForLength(winner.length);
      const stats = this.statsBySnakeId.get(winner.id);
      if (stats) stats.foodCollected += 1;
      this.foodsById.delete(food.id);
    }
  }

  private applyResonanceTide(): void {
    const radius = this.resonanceRadius();
    const halfBand = this.config.resonanceBandWidth / 2;
    for (const food of this.foodsById.values()) {
      if (food.source !== "natural") continue;
      if (Math.abs(distance(food.position, vec(0, 0)) - radius) <= halfBand) {
        food.resonantUntil = Math.max(
          food.resonantUntil,
          this.elapsedSeconds + this.config.resonanceDurationSeconds,
        );
      }
    }
  }

  private replenishNaturalFood(): void {
    let naturalCount = [...this.foodsById.values()].filter(
      (food) => food.source === "natural",
    ).length;
    while (
      naturalCount < this.config.naturalFoodCount &&
      this.foodsById.size < MAX_FOOD_ENTITIES
    ) {
      const angle = this.random.between(-Math.PI, Math.PI);
      const radius =
        Math.sqrt(this.random.next()) * (this.config.arenaRadius - 50);
      this.addGeneratedFood({
        position: scale(fromAngle(angle), radius),
        radius: 3,
        baseValue: this.random.next() < 0.12 ? 2 : 1,
        source: "natural",
        resonantUntil: 0,
      });
      naturalCount += 1;
    }
  }

  private resonanceRadius(): number {
    const cycle = this.elapsedSeconds % this.config.resonancePeriodSeconds;
    return (
      (cycle / this.config.resonancePeriodSeconds) * this.config.arenaRadius
    );
  }

  private addGeneratedFood(food: Omit<Food, "id">): void {
    if (this.foodsById.size >= MAX_FOOD_ENTITIES) {
      const candidates = this.sortedFoods().sort(
        (left, right) =>
          Number(left.source === "natural") -
            Number(right.source === "natural") ||
          distanceSquared(left.position, food.position) -
            distanceSquared(right.position, food.position) ||
          left.id.localeCompare(right.id),
      );
      const target = candidates[0];
      if (!target) return;
      const source =
        target.source === food.source
          ? target.source
          : target.source === "elimination" || food.source === "elimination"
            ? "elimination"
            : "boost";
      this.foodsById.set(target.id, {
        ...target,
        source,
        radius: Math.max(target.radius, food.radius),
        baseValue: target.baseValue + food.baseValue,
        resonantUntil: source === "natural" ? target.resonantUntil : 0,
      });
      return;
    }
    let id: string;
    do {
      this.generatedEntitySequence += 1;
      id = `generated-${String(this.generatedEntitySequence).padStart(8, "0")}`;
    } while (this.foodsById.has(id));
    this.foodsById.set(id, { id, ...food, position: { ...food.position } });
  }

  private headRadius(snake: SnakeState): number {
    return snake.width * this.config.headRadiusFactor;
  }

  private sortedSnakes(): SnakeState[] {
    return [...this.snakesById.values()].sort(byId);
  }

  private sortedFoods(): Food[] {
    return [...this.foodsById.values()].sort(byId);
  }

  private rankings(): MatchRanking[] {
    return buildRankings(this.sortedSnakes(), false);
  }

  private resultRankings(): MatchRanking[] {
    return buildRankings(this.sortedSnakes(), true);
  }
}
